/**
 * @file ImpersonationStartModal.tsx
 * @description Окно «Войти как пользователь» (phase08.5 shadcn; phase19.28
 * этап А — обязательная причина входа, PL17).
 *
 * Причина (5–300 знаков) обязательна и уходит в `POST /admin/impersonation/start`
 * — сервер пишет её в журнал магазина и платформы. `shopId` сужает выбор
 * кабинета до одного магазина: кнопка «Кабинет ↗» в карточке магазина
 * входит под владельцем именно этого магазина. Висящий прежний вход —
 * `ImpersonationOrphanNotice` (подключиться или завершить).
 */

import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { ImpersonationContextPick } from "./ImpersonationContextPick";
import { ImpersonationOrphanNotice } from "./ImpersonationOrphanNotice";
import { ImpersonationReasonField } from "./ImpersonationReasonField";
import { impersonationReasonIssue, normalizeImpersonationReason } from "../lib/impersonation-reason";
import { Alert } from "./ui/alert";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  adoptImpersonation,
  getActiveImpersonation,
  startImpersonation,
  stopImpersonation,
  type ActiveImpersonation,
} from "../lib/api-impersonation";
import { ApiError } from "../lib/api-client";
import type { AdminUser } from "../lib/api-admin-users";

interface Props {
  open: boolean;
  user: AdminUser;
  /** Только кабинет этого магазина (вход из карточки магазина). */
  shopId?: string;
  onClose: () => void;
  onStarted: () => void;
}

export function ImpersonationStartModal({ open, user, shopId, onClose, onStarted }: Props) {
  const eligibleContexts = user.contexts.filter(
    (c) => c.contextType === "SHOP" && (!shopId || c.shop?.id === shopId),
  );

  const [contextId, setContextId] = useState<string>(eligibleContexts[0]?.id ?? "");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [orphan, setOrphan] = useState<ActiveImpersonation | null>(null);
  const [checkingOrphan, setCheckingOrphan] = useState(true);
  const [orphanAction, setOrphanAction] = useState<null | "adopt" | "stop">(null);

  useEffect(() => {
    if (!open) return;
    // Каждое открытие — с чистой причиной: она про этот вход, не про прошлый.
    setCheckingOrphan(true);
    setError(null);
    setReason("");
    setReasonError(null);
    getActiveImpersonation()
      .then((s) => setOrphan(s))
      .catch(() => setOrphan(null))
      .finally(() => setCheckingOrphan(false));
  }, [open]);

  async function onAdoptOrphan() {
    if (!orphan) return;
    setOrphanAction("adopt");
    setError(null);
    try {
      await adoptImpersonation(orphan.sessionId);
      window.location.assign(import.meta.env.BASE_URL);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось подключиться");
      setOrphanAction(null);
    }
  }

  async function onStopOrphan() {
    setOrphanAction("stop");
    setError(null);
    try {
      await stopImpersonation();
      setOrphan(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось завершить");
    } finally {
      setOrphanAction(null);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const issue = impersonationReasonIssue(reason);
    setReasonError(issue);
    if (!contextId) {
      setError("Выберите кабинет пользователя.");
      return;
    }
    if (issue) return;
    setSubmitting(true);
    try {
      await startImpersonation({ targetUserId: user.id, targetContextId: contextId, mode: "ACT", reason: normalizeImpersonationReason(reason) });
      onStarted();
    } catch (err) {
      // 422 impersonation_reason_required / _too_long — под полем причины.
      if (err instanceof ApiError && err.code.startsWith("impersonation_reason")) {
        setReasonError(impersonationReasonIssue(reason) ?? err.message);
      } else setError(err instanceof ApiError ? err.message : "Не удалось войти как пользователь");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Войти как «{user.displayName || user.email}»</DialogTitle>
        </DialogHeader>

        {checkingOrphan ? (
          <div className="flex justify-center py-6">
            <Loader2 className="size-6 animate-spin text-primary" aria-label="Проверяем прежний вход" />
          </div>
        ) : orphan ? (
          <ImpersonationOrphanNotice
            orphan={orphan}
            error={error}
            action={orphanAction}
            onAdopt={() => void onAdoptOrphan()}
            onStop={() => void onStopOrphan()}
          />
        ) : (
          <form id="impersonation-start-form" onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-3" noValidate>
            <p className="text-sm text-on-surface-variant">
              Вы войдёте в кабинет другого пользователя и сможете менять настройки. Все действия пишутся в журнал
              от вашего имени как фактического исполнителя.
            </p>

            <ImpersonationContextPick
              contexts={eligibleContexts}
              shopScoped={!!shopId}
              value={contextId}
              onChange={setContextId}
              disabled={submitting}
            />

            <ImpersonationReasonField
              value={reason}
              onChange={(next) => {
                setReason(next);
                if (reasonError) setReasonError(impersonationReasonIssue(next));
              }}
              error={reasonError}
              disabled={submitting}
            />

            {error ? <Alert severity="error">{error}</Alert> : null}
          </form>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting || orphanAction !== null}>
            {orphan ? "Закрыть" : "Отмена"}
          </Button>
          {!orphan && !checkingOrphan ? (
            <Button
              type="submit"
              form="impersonation-start-form"
              loading={submitting}
              disabled={eligibleContexts.length === 0}
            >
              Войти
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
